/**
 * Prepares a client-selected file for attachment to a Web3Forms submission.
 *
 * Web3Forms' built-in uploader accepts a single file per field, up to 5 MB.
 * Phone photographs of a passport routinely exceed that, so images are
 * downscaled in the browser until they fit. Anything else that is too large
 * is rejected -- clause 2 of the terms offers email and WhatsApp as
 * alternatives, so a rejection is not a dead end.
 */

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';

/** Longest edge to try, in order, when shrinking an oversized image. */
const WIDTH_STEPS = [2400, 1800, 1400, 1100, 900];
const JPEG_QUALITY = 0.82;

export const isImage = file => typeof file.type === 'string' && file.type.startsWith('image/');

export const formatSize = bytes => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const tooLargeMessage = file =>
  `${file.name} is ${formatSize(file.size)}, over the ${formatSize(MAX_UPLOAD_BYTES)} limit. ` +
  `Please send this one by email or WhatsApp instead — both are linked above.`;

const loadImage = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('could not read image'));
    };
    img.src = url;
  });

const toBlob = (canvas, quality) =>
  new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));

const renameAsJpeg = name => name.replace(/\.[^.]+$/, '') + '.jpg';

/**
 * Returns { file, downscaled } where file is within MAX_UPLOAD_BYTES.
 * Throws an Error (message safe to show the client) when that is impossible.
 */
export async function prepareUpload(file) {
  if (file.size <= MAX_UPLOAD_BYTES) {
    return { file, downscaled: false };
  }

  if (!isImage(file)) {
    throw new Error(tooLargeMessage(file));
  }

  // Canvas is unavailable in some environments (jsdom, older browsers); fall
  // back to the same rejection the client would get for an oversized PDF.
  const canvas = typeof document !== 'undefined' && document.createElement('canvas');
  if (!canvas || typeof canvas.getContext !== 'function' || typeof canvas.toBlob !== 'function') {
    throw new Error(tooLargeMessage(file));
  }

  let img;
  try {
    img = await loadImage(file);
  } catch {
    throw new Error(tooLargeMessage(file));
  }

  for (const maxEdge of WIDTH_STEPS) {
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // eslint-disable-next-line no-await-in-loop
    const blob = await toBlob(canvas, JPEG_QUALITY);
    if (blob && blob.size <= MAX_UPLOAD_BYTES) {
      return {
        file: new File([blob], renameAsJpeg(file.name), { type: 'image/jpeg' }),
        downscaled: true,
      };
    }
  }

  throw new Error(tooLargeMessage(file));
}
